import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAudio } from "../Contexts/AudiosContext";
import RabbitLyrics from "rabbit-lyrics";
import "../Styles/AudioPlayer.css";
import lyricsnotfound from "../Images/lyricsnotfound.png";
import { LogInModel } from "../Pages/LoginModel";

/** Div lyrics container — RabbitLyrics binds its controller on this node */
type LyricsEl = HTMLDivElement & {
  rabbitLyrics?: RabbitLyrics;
};

const AudioLyrics = () => {
  const { t } = useTranslation();
  const { currentAudio, message, lyricsText, refAudio, reloadLyrics } = useAudio();
  const refLrc = React.useRef<LyricsEl | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [logIn, setLogIn] = useState(false);

  const isLoggedIn = !!(localStorage.getItem("Token") && localStorage.getItem("UserId"));
  // Audio has lyrics available on server
  const hasLyricsMeta = currentAudio?.lyricsHash != null;
  const currentAudioId = currentAudio?.id;

  useEffect(() => {
    // Reset initialized state on audio or lyrics change
    setIsInitialized(false);

    const logdetails: string[] = [];
    logdetails.push(`audioId=${currentAudioId || "none"}`);
    logdetails.push(`lyricsLength=${lyricsText?.length || 0}`);
    logdetails.push(`isLoggedIn=${isLoggedIn}`);

    if (!currentAudioId || !lyricsText || !isLoggedIn) {
      logdetails.push("action=skip_init");
      console.log("[AudioLyrics]", logdetails.join(" | "));
      return;
    }
    logdetails.push("action=attempt_init");

    let mounted = true;
    const audioElId = `audio-${currentAudioId}`;

    /**
     * RabbitLyrics stores itself on the DOM node and returns that instance on
     * later `new RabbitLyrics(...)` calls without re-parsing. After a same-page
     * track change we must call setLyrics() (or clear the binding) so LRC is
     * rendered as rows again instead of a raw paragraph with timestamps.
     */
    const init = () => {
      if (!mounted) return false;
      const audioEl =
        (refAudio && refAudio.current) ||
        (document.getElementById(audioElId) as HTMLMediaElement | null);
      const lyricsEl = refLrc.current;
      if (!audioEl || !lyricsEl) {
        logdetails.push(`wait=audio_or_ref_not_ready id=${audioElId}`);
        return false;
      }

      try {
        const text = lyricsText || "";
        const existing = lyricsEl.rabbitLyrics;

        if (existing && typeof existing.setLyrics === "function") {
          // Same lyrics container reused (same audio id / re-render) — update content only
          existing.setLyrics(text);
          setIsInitialized(true);
          logdetails.push("mode=setLyrics_existing");
          console.log("[AudioLyrics]", logdetails.join(" | "));
          return true;
        }

        // Fresh bind: pass lyrics via options so parse/render always runs
        delete lyricsEl.rabbitLyrics;
        lyricsEl.textContent = text;
        new RabbitLyrics(lyricsEl, audioEl, { lyrics: text });
        setIsInitialized(true);
        logdetails.push("mode=new_RabbitLyrics");
        console.log("[AudioLyrics]", logdetails.join(" | "));
        return true;
      } catch (err) {
        logdetails.push(`error=${err instanceof Error ? err.message : String(err)}`);
        console.error("[AudioLyrics]", logdetails.join(" | "), err);
        return false;
      }
    };

    let retries = 0;
    const maxRetries = 20;
    const retryInterval = 100;

    if (!init()) {
      const timer = setInterval(() => {
        retries++;
        if (init() || retries >= maxRetries) {
          if (retries >= maxRetries) {
            logdetails.push(`failed_after_retries=${retries}`);
            console.warn("[AudioLyrics]", logdetails.join(" | "));
          }
          clearInterval(timer);
        }
      }, retryInterval);
      return () => {
        mounted = false;
        clearInterval(timer);
      };
    }
    return () => {
      mounted = false;
    };
    // Depend on audio id + lyrics text so track changes re-bind without
    // re-running on every currentAudio object reference update.
  }, [currentAudioId, lyricsText, refAudio, isLoggedIn]);

  const closeLoginModal = () => setLogIn(false);

  const handleLoginStateChange = (newState: any) => {
    console.log("[AudioLyrics] login state changed", newState);
    if (newState === true) {
      setLogIn(false);
      // Load lyrics now that user has Token/SignKey
      if (typeof reloadLyrics === "function") {
        reloadLyrics();
      }
    }
  };

  // --- Render branches ---
  // 1) No lyrics on this audio → existing "not available" image
  // 2) Has lyrics but guest → please login to see lyrics
  // 3) Logged in + lyrics → show lyrics panel
  let lyricsBody: React.ReactNode;

  if (!hasLyricsMeta) {
    lyricsBody = (
      <div style={{ textAlign: "center" }}>
        <img
          alt="lyricsnotfound"
          src={lyricsnotfound}
          className="lyricsnotfoundstyle"
        />
      </div>
    );
  } else if (!isLoggedIn) {
    // Guest: fill shared lyrics frame height (CSS .audio-lyrics-guest-prompt)
    lyricsBody = (
      <div className="audio-lyrics-guest-prompt">
        <p>{t("please_login_to_see_lyrics_tr")}</p>
        <button
          type="button"
          className="audio-lyrics-guest-login-btn"
          onClick={() => {
            console.log("[AudioLyrics] guest clicked login for lyrics");
            setLogIn(true);
          }}
        >
          {t("login_tr")}
        </button>
      </div>
    );
  } else {
    // Logged in: lyrics panel fills shared frame; RabbitLyrics scrolls inside
    lyricsBody = (
      <div className="audio-lyrics-logged-in">
        <div className="audio-lyrics-logged-in-inner">
          <div
            ref={refLrc}
            key={currentAudio?.id}
            className="lyrics"
            style={{
              fontSize: "25px",
              padding: "10px",
              fontFamily: "ChanakyaUni",
            }}
          />
          {/* Inline wait — do not use #preloader (full-page white) */}
          {!isInitialized ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "22px",
                color: "#ff8a00",
                fontFamily: "ChanakyaUni",
                background: "rgba(255,255,255,0.55)",
              }}
            >
              {lyricsText ? "" : "..."}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="audio-lyrics-body">
      {lyricsBody}
      {message ? (
        <div
          style={{
            textAlign: "center",
            zIndex: 1,
            position: "absolute",
            top: "85%",
            backgroundColor: "#ffa820",
            borderRadius: "10px",
          }}
        >
          <p
            style={{
              fontSize: "22px",
              fontFamily: "ChanakyaUni",
              margin: "0px 10px 0 5px",
              color: "#fff",
            }}
          >
            {message}
          </p>
        </div>
      ) : null}
      <LogInModel
        opens={logIn}
        onCloses={closeLoginModal}
        onLoginStateChange={handleLoginStateChange}
      />
    </div>
  );
};
export default AudioLyrics;
